import { Box, Card, CardContent, Typography, Button, CircularProgress } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useCommonStyles } from '../../styles/GenericStyle';
// import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { useFetchMaterialDetails, useGetSPLotbyId, useGetNotRegisteredCount } from '../../api/vegApl';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { INSTRUCTIONS, scannerIcon, containerIcon, buttonSVG } from './constants';
import { mintRegistrationStyles } from './styles';
import { RegisterToMintDialog } from './RegisterToMintDialog';
import { useGlobalState } from "../../contexts/GlobalState";
import ScanInput from '../ScanInput';

// MINT registration is allowed from Packaging & Labelling onward, so materials need not wait for the whole SPLot.
const isMintRegistrationAllowedStep = (stepName: unknown) => {
    const normalized = String(stepName ?? '').trim().toLowerCase();
    const isPackaging = normalized.includes('packaging') && (normalized.includes('labelling') || normalized.includes('label'));
    const isMintRegistration = normalized.includes('mint') && normalized.includes('registration');
    return isPackaging || isMintRegistration;
};

export const MintRegistrationComponent = () => {
    const classes = useCommonStyles();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const inputRef = useRef<HTMLInputElement>(null);
    const [scannedValue, setScannedValue] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [materialDetails, setMaterialDetails] = useState<any>(null);
    const { state: { siteSelected } = {}} = useGlobalState();
    const reqObj = { siteCode:siteSelected  };

    const { data: notRegisteredCount = 0, refetch: refetchNotRegisteredCount } =
        useGetNotRegisteredCount(reqObj, {
            query: {
                queryKey: ['notRegisteredCount'],
                 enabled: false,                select: (response) => {                                  
                    return response?.data ?? 0;
                },

                //refetchOnWindowFocus: false
            }
        });


   useEffect(() => {
       siteSelected && refetchNotRegisteredCount();
    }, [siteSelected]);

    const { mutate: fetchMaterialDetails, isPending: isFetching } = useFetchMaterialDetails({
        mutation: {
            onSuccess: (response: any) => {
                const data = response?.data;

                if (!data || !Array.isArray(data) || data.length === 0) {
                    showSnackbar({ message: 'No material found with given container ID', severity: 'error' });
                    return;
                }
                if (data[0]?.['Current Status'] !== "Processing") {
                    showSnackbar({ message: 'Registration failed. Material not in processing status', severity: 'error' });
                    return;
                }
                setMaterialDetails(data);
                getSPLotbyId({ splotId: data[0]?.splot_id.toString() });
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.detail || 'Failed to fetch material details';
                showSnackbar({ message: errorMessage, severity: 'error' });
            }
        }
    });

    const { mutate: getSPLotbyId } = useGetSPLotbyId({
        mutation: {
            onSuccess: (response: any) => {
                let status = response?.data?.[0]?.status;
                if (isMintRegistrationAllowedStep(status)) {
                    setOpenDialog(true);
                } else {
                    showSnackbar({ message: `Registration failed. SPLot not in Packaging & Labelling or MINT registration step.`, severity: 'error' });
                }
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.detail || 'Failed to fetch material details';
                showSnackbar({ message: errorMessage, severity: 'error' });
            }
        }
    });

    const handleScan = () => {
        if (!scannedValue.trim()) return;
        const trimmedValue = scannedValue.trim();
        // Validate alphanumeric (letters and numbers only)
        const alphanumericRegex = /^[a-zA-Z0-9]+$/;
        if (!alphanumericRegex.test(trimmedValue)) {
            showSnackbar({ message: 'Please enter a valid container ID', severity: 'error' });
            return;
        }
        fetchMaterialDetails({ data: { container_alt_ids: [trimmedValue] } });
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setMaterialDetails(null);
        setScannedValue('');
    };

    // const handleKeyPress = (e: React.KeyboardEvent) => e.key === 'Enter' && handleScan();

    const handleScanInputEnter = () => handleScan();

    useEffect(() => {
        inputRef.current?.focus();          
    }, []);

    return (
        <div data-testid="MintRegistration">
            <Box sx={{ p: 2 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography component="span" sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', mb: 1 }}
                        className={classes.labelNoto16Bold700} onClick={() => navigate(-1)}>
                        <ArrowBackIcon sx={{ verticalAlign: 'middle', transform: 'scaleX(1.3)', transformOrigin: 'center left', mr: 0.5 }} />
                        <Typography component="span" className={classes.labelNoto20Bold600}>Back</Typography>
                    </Typography>
                    <Typography component="h1" className={classes.labelNoto36Bold700} sx={{ letterSpacing: 0.4 }}>
                        MINT Registration
                    </Typography>
                </Box>

                <Card variant="outlined" sx={mintRegistrationStyles.pendingCard}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box dangerouslySetInnerHTML={{ __html: containerIcon }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary" className={classes.labelInter14Bold400}>
                                Containers pending MINT Registration
                            </Typography>
                            <Typography variant="h4" className={classes.labelNoto36Bold700} sx={{ mt: 0.5 }}>{notRegisteredCount}</Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                            <Box sx={{ mb: 2 }} dangerouslySetInnerHTML={{ __html: scannerIcon }} />
                            <Typography variant="h5" className={classes.labelNoto24Bold400} sx={{ mb: 1 }}>
                                Scan Container
                            </Typography>
                            <Typography variant="body1" color="text.secondary" className={classes.labelInter16Bold400}>
                                Enter or scan Container ID to register with MINT
                            </Typography>
                        </Box>

                        <Box sx={mintRegistrationStyles.inputContainer}>
                            <ScanInput
                                placeholder="Scan or Enter Container ID"
                                defaultValue={scannedValue}
                                onChange={(newVal) => setScannedValue(newVal)}
                                onEnterPress={handleScanInputEnter}
                                onInputChange={() => {}}
                                manualEdit={true}
                                ref={inputRef}
                                label=""
                                showScanButton={false}
                            />
                            <Button variant="contained" disableRipple onClick={handleScan} className={classes.greenButton}
                                startIcon={<Box sx={{ fontSize: "12px" }} dangerouslySetInnerHTML={{ __html: buttonSVG }} />}
                                sx={() => ({
                                    ...mintRegistrationStyles.scanButton
                                })}

                            >
                                {isFetching ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Scan'}
                            </Button>
                        </Box>

                        <Box sx={{ mt: 4, pl: 2, maxWidth: 800 }}>
                            <Box component="ol" sx={mintRegistrationStyles.instructionsList}>
                                {INSTRUCTIONS.map((instruction, idx) => (
                                    <li key={idx}>
                                        <Typography variant="body2" className={classes.labelInter14Bold400} color="text.secondary">
                                            {instruction}
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <RegisterToMintDialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    materialDetails={materialDetails}
                    onSuccess={() => refetchNotRegisteredCount()}
                />
            </Box>
        </div>
    );
};
